import { bankAccountService } from '../services/api';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  _count?: {
    transactions: number;
  };
}

const useBankAccountStoreBase = createEntityStore<BankAccount>({
  entityName: 'bankAccount',
  fetchFn: (page: number) => bankAccountService.getBankAccounts(page, 20),
  deleteFn: bankAccountService.deleteBankAccount,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

export const useBankAccountStore = (onBalanceChange?: () => void) => {
  const store = useBankAccountStoreBase();

  const confirmDeleteWithBalanceUpdate = async () => {
    await store.confirmDelete();
    if (onBalanceChange) {
      onBalanceChange();
    }
  };

  return {
    accounts: store.items,
    pagination: store.pagination,
    currentPage: store.currentPage,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    itemToDeleteId: store.itemToDeleteId,
    isDeleting: store.isDeleting,
    fetchAccounts: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: confirmDeleteWithBalanceUpdate,
    closeDeleteModal: store.closeDeleteModal,
    refreshData: store.refreshData,
    addAccount: (account: BankAccount) => {
      store.addItem(account);
    },
    updateAccount: (id: string, account: Partial<BankAccount>) => {
      store.updateItem(id, account);
    }
  };
};

// 導出基礎 store 供其他地方使用
export { useBankAccountStoreBase };

// 創建持久化的銀行帳戶統計 store
interface BankAccountStatsState {
  stats: { totalBalance: number; totalAccounts: number; activeAccounts: number } | null;
  lastFetchTime: number | null;
  fetchStats: (forceRefresh?: boolean) => Promise<void>;
}

export const useBankAccountStatsStore = create<BankAccountStatsState>()(
  persist(
    (set, get) => ({
      stats: null as { totalBalance: number; totalAccounts: number; activeAccounts: number } | null,
      lastFetchTime: null as number | null,
      fetchStats: async (forceRefresh = false) => {
        const state = get();

        if (forceRefresh || !state.stats || !state.lastFetchTime) {
          try {
            const response = await bankAccountService.getBankAccountSummary();
            set({
              stats: response.data,
              lastFetchTime: Date.now()
            });
          } catch (error) {
            console.error('統計失敗:', error);
            set({ stats: { totalBalance: 0, totalAccounts: 0, activeAccounts: 0 } });
          }
        }
      },
    }),
    {
      name: 'bank-account-stats-storage',
      version: 1,
    }
  )
);

// 創建持久化的銀行帳戶 store
interface PersistentBankAccountState {
  selectedAccountId: string;
  setSelectedAccountId: (accountId: string) => void;
  clearSelectedAccount: () => void;
}

export const usePersistentBankAccountStore = create<PersistentBankAccountState>()(
  persist(
    (set, get) => ({
      selectedAccountId: '',
      setSelectedAccountId: (accountId: string) => set({ selectedAccountId: accountId }),
      clearSelectedAccount: () => set({ selectedAccountId: '' }),
    }),
    {
      name: 'bank-account-storage',
      version: 1,
    }
  )
);

// 創建銀行帳戶詳情快取 store
interface BankAccountDetailState {
  accountCache: Record<string, { data: BankAccount; lastFetchTime: number }>;
  getAccountById: (accountId: string, forceRefresh?: boolean) => Promise<BankAccount>;
  clearAccountDetailCache: (accountId: string) => void;
}

export const useBankAccountDetailStore = create<BankAccountDetailState>()(
  persist(
    (set, get) => ({
      accountCache: {} as Record<string, { data: BankAccount; lastFetchTime: number }>,

      // 根據ID獲取帳戶詳情
      getAccountById: async (accountId: string, forceRefresh = false) => {
        const state = get();
        const cached = state.accountCache[accountId];

        // 如果有快取且不是強制刷新
        if (!forceRefresh && cached) {
          return cached.data;
        }

        try {
          // 從列表中獲取帳戶詳情
          const response = await bankAccountService.getBankAccounts(1, 100);
          const account = response.data.data.find((acc: BankAccount) => acc.id === accountId);

          if (!account) {
            throw new Error('帳戶不存在');
          }

          // 更新快取
          set(state => ({
            accountCache: {
              ...state.accountCache,
              [accountId]: {
                data: account,
                lastFetchTime: Date.now()
              }
            }
          }));

          return account;
        } catch (error) {
          console.error('載入帳戶詳情失敗:', error);
          throw error;
        }
      },

      // 清除特定帳戶的快取
      clearAccountDetailCache: (accountId: string) => {
        set(state => {
          const newCache = { ...state.accountCache };
          delete newCache[accountId];
          return { accountCache: newCache };
        });
      }
    }),
    {
      name: 'bank-account-detail-storage',
      version: 1,
    }
  )
);

// 創建銀行交易記錄 store 的緩存系統
const transactionStoreCache: Record<string, any> = {};

export const useBankTransactionStore = (accountId: string) => {
  // 使用緩存避免重複創建 store，為每個 accountId 創建獨立的 store 實例
  if (!transactionStoreCache[accountId]) {
    const store = createEntityStore<any>({
      entityName: `交易記錄-${accountId}`, // 使用 accountId 作為 entityName 的一部分，確保每個帳戶的交易記錄快取獨立
      fetchFn: (page: number) => bankAccountService.getBankTransactions(accountId, page, 20),
      deleteFn: (id: string) => bankAccountService.deleteBankTransaction(accountId, id),
      toast
    });
    transactionStoreCache[accountId] = store;
  }

  return transactionStoreCache[accountId]();
};

